import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { currencyFlagUrl } from 'src/app/shared/helpers/currency-flags';
import { InvoiceShort } from '../../models/invoice/invoice-short';
import { InvoiceStatus } from '../../enums/invoice-status';
import { Router } from '@angular/router';
import { inProcessingRecord } from '../../data/in-processing-record';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { RecognizedInvoiceService } from '../../services/recognized-invoice.service';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { Invoice } from '../../models/invoice/invoice';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { take } from 'rxjs/operators';
import { InboxInvoicesStorageService } from '../../services/inbox-invoices-storage.service';
import { SortTableParameters } from '../../models/filter/sort-table-parameters';
import { Tag } from 'src/app/data/model/tag';
import { DeletedTagFromInvoice } from '../../models/tag/deleted-tag';
import { Guid } from 'guid-typescript';
import { TrackingCategory } from '../../models/tracking-categories.ts/trackingCategory';
import { BookkeepingFilter } from '../../models/filter/bookkeeping-filter';
import { ScreenBrakepoints } from 'src/app/data/model/screen-brakepoints';
import * as $ from 'jquery';
import { PaymentAccount } from '../../models/account/payment-account';
import { CreatedInvoiceWithLines } from '../../models/invoice/created-invoice-with-lines';
import { UnreconciledReport } from '../../models/invoice/unreconciled-report';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { CurrencyCode } from '../../enums/currency-code';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { Subscription } from 'rxjs';
import { fromCamelCase } from 'src/app/core/util/camel-case-converter';

export type StatusType = 'Awaiting Payment' | 'Paid' | 'Voided';
export type IntegrationType = 'Xero' | 'QBO' | 'None';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class TableComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() invoices: InvoiceShort[] = [];
  @Input() currentFilters: BookkeepingFilter;
  @Input() suppliers: any[] = [];
  @Input() tags: any[] = [];
  @Input() submitters: any[] = [];
  @Input() assignedTo: any[] = [];
  @ViewChild('headerFilterPopover') headerFilterPopover: ElementRef;
  @Input() status: InvoiceStatus;
  @Input() emptyMessage: string = 'No data to display';
  @Input() maxCount: number = 0;
  // When true the table renders every loaded row and hides the pager footer —
  // the parent appends rows via infinite scroll instead of paging.
  @Input() infiniteScroll: boolean = false;
  @Input() pageSize: number = 25;
  @Input() page: number = 1;
  @Input() pageSizeOptions: number[] = [25, 50, 100, 250];
  @Input() screenBrakepoint: ScreenBrakepoints =
    ScreenBrakepoints.MinWidth769px;

  @Input() selectedTrackingCategories: TrackingCategory[] = [];
  @Input() accounts: PaymentAccount[] = [];
  selectableAccounts: PaymentAccount[] = [];
  @Input() showSubmitterColumn: boolean = true;
  @Input() canPublish: boolean = true;
  @Input() columnVisibility: Record<string, boolean> = {};

  @Output() pageSelect = new EventEmitter<number>();
  @Output() pageSizeSelect = new EventEmitter<number>();
  @Output() sortTableEvent = new EventEmitter<SortTableParameters>();
  @Output() deleteTagEvent = new EventEmitter<DeletedTagFromInvoice>();
  @Output() rowCheckboxClickEvent = new EventEmitter<void>();
  @Output() publishInvoiceEvent = new EventEmitter<InvoiceShort>();
  // Emits when the user applies a header inline filter
  @Output() headerFilterApplied = new EventEmitter<{ field: string, value: any }>();
  // Row clicked — parent opens the invoice detail as an in-page overlay
  // (query-param `id`) instead of navigating to the full-page editInvoice route.
  @Output() openInvoice = new EventEmitter<{ id: string; editMode: boolean }>();

  @ViewChild('BookkeepingTable') table: any;

  // Inline header filter state
  activeHeaderFilterField: string = null;
  popoverStyle: any = {};
  // local selections
  headerSelectedValues: any[] = [];
  headerAmountMin: number = null;
  headerAmountMax: number = null;
  // computed options for inline popovers
  supplierOptions: string[] = [];
  supplierDisplayOptions: string[] = [];
  supplierFilteredOptions: string[] = [];
  supplierSearchTerm: string = '';

  tagsFilteredOptions: string[] = [];
  tagsSearchTerm: string = '';

  submitterOptions: string[] = [];
  submitterFilteredOptions: string[] = [];
  submitterSearchTerm: string = '';

  typeOptions: string[] = [];
  typeFilteredOptions: string[] = [];
  typeSearchTerm: string = '';

  // Categories (accounts) filtered list for popover
  categoriesFilteredOptions: string[] = [];
  // appended popover state
  private popoverAppended: boolean = false;
  private outsideClickHandler: any = null;
  onHeaderFilterClick(field: string, event?: Event) {
    if (event) {
      // prevent table row click handlers from firing when clicking header filters
      event.stopPropagation();
    }

    // Toggle same field
    if (this.activeHeaderFilterField === field) {
      this.activeHeaderFilterField = null;
      return;
    }

    this.activeHeaderFilterField = field;

    // prepare selections
    this.headerSelectedValues = [];
    this.headerAmountMin = null;
    this.headerAmountMax = null;

    // Reset search terms and ensure filtered option lists are populated so the popover shows proper choices
    this.supplierSearchTerm = '';
    this.tagsSearchTerm = '';
    this.typeSearchTerm = '';
    this.submitterSearchTerm = '';

    // initialize filtered lists if not already
    this.filterSupplierOptions(this.supplierSearchTerm || '');
    this.filterTagOptions(this.tagsSearchTerm || '');
    this.filterTypeOptions(this.typeSearchTerm || '');
    this.filterSubmitterOptions(this.submitterSearchTerm || '');

    // Prefill from current filters (if provided)
    if (this.currentFilters) {
      switch (field) {
        case 'supplier':
          this.headerSelectedValues = this.currentFilters.suppliers ? [...this.currentFilters.suppliers] : [];
          break;
        case 'category':
          this.headerSelectedValues = this.currentFilters.categories ? [...this.currentFilters.categories] : [];
          break;
        case 'tags':
          this.headerSelectedValues = this.currentFilters.tags ? [...this.currentFilters.tags] : [];
          break;
        case 'tracking1':
          this.headerSelectedValues = this.currentFilters.trackingOptions1 ? [...this.currentFilters.trackingOptions1] : [];
          break;
        case 'tracking2':
          this.headerSelectedValues = this.currentFilters.trackingOptions2 ? [...this.currentFilters.trackingOptions2] : [];
          break;
        case 'amount':
          this.headerAmountMin = this.currentFilters.amountMin ?? null;
          this.headerAmountMax = this.currentFilters.amountMax ?? null;
          break;
        case 'date':
          if (this.currentFilters.invoiceDateRange) {
            this.headerSelectedValues = [this.currentFilters.invoiceDateRange[0], this.currentFilters.invoiceDateRange[1]];
          }
          break;
        case 'type':
          this.headerSelectedValues = this.currentFilters.types ? [...this.currentFilters.types] : [];
          break;
      }
    }

    // if switching headers, make sure to cleanup previous appended popover so we don't leave a stale zero-sized element
    if (this.activeHeaderFilterField && this.activeHeaderFilterField !== field) {
      this.closeHeaderFilterPopover();
    }

    // position popover under the clicked header
    setTimeout(() => {
      const headerEl = (event && (event.target as HTMLElement).closest('.column-header')) as HTMLElement;
      const tableEl = this.table?.element?.nativeElement as HTMLElement;

      // Ensure filtered lists are prepared for the field so the popover has content
      if (field === 'category') {
        this.categoriesFilteredOptions = (this.accounts || []).map(a => a.name).filter(s => !!s).sort((a,b) => (a||'').localeCompare(b||''));
      }

      if (headerEl) {
        const headerRect = headerEl.getBoundingClientRect();
        // Use viewport coordinates and fixed positioning so the popover is not clipped by overflow
        const left = Math.round(headerRect.left + 4);
        const top = Math.round(headerRect.bottom + 6);
        this.popoverStyle = {
          left: `${left}px`,
          top: `${top}px`,
          position: 'fixed'
        };
      } else if (event && (event as MouseEvent).clientX !== undefined) {
        // Fallback: use mouse coordinates and fixed positioning
        const me = event as MouseEvent;
        const left = me.clientX;
        const top = me.clientY + 8;
        this.popoverStyle = {
          left: `${left}px`,
          top: `${top}px`,
          position: 'fixed'
        };
      } else {
        this.popoverStyle = { left: '0px', top: '40px', position: 'fixed' };
      }

      // Debug: log some state so we can see why a popover might show empty
      console.log('Opening header popover for', field, 'prefill:', this.headerSelectedValues, 'supplierFiltered:', this.supplierFilteredOptions?.length, 'tags:', this.tagsFilteredOptions?.length, 'types:', this.typeFilteredOptions?.length, 'categories:', this.categoriesFilteredOptions?.length);
    }, 5);

    // Debug: print computed popover position and inspect DOM via ViewChild
    setTimeout(() => {
      // ensure view updates so *ngIfed popover is created
      try {
        this._cd.detectChanges();
      } catch (e) {
        // ignore detection errors in test environment
      }

      if (this.headerFilterPopover && this.headerFilterPopover.nativeElement) {
        const el = this.headerFilterPopover.nativeElement as HTMLElement;
        // If not appended to body already, move it to body so it won't be clipped
        if (!this.popoverAppended) {
          try {
            document.body.appendChild(el);
            this.popoverAppended = true;
            // ensure it uses fixed positioning and the computed styles are applied
            el.style.position = this.popoverStyle.position || 'fixed';
            el.style.left = this.popoverStyle.left || el.style.left;
            el.style.top = this.popoverStyle.top || el.style.top;
            el.style.display = 'block';

            // Clamp into viewport after it has rendered
            setTimeout(() => {
              const rect = el.getBoundingClientRect();
              const popWidth = rect.width || 360;
              const popHeight = rect.height || 200;
              let leftPx = parseInt(el.style.left || '0', 10);
              let topPx = parseInt(el.style.top || '0', 10);

              // If element uses fixed positioning and left/top are viewport coords, clamp them
              const margin = 8;
              if (leftPx + popWidth > window.innerWidth - margin) {
                leftPx = Math.max(margin, window.innerWidth - popWidth - margin);
                el.style.left = `${leftPx}px`;
              }
              if (topPx + popHeight > window.innerHeight - margin) {
                // try placing above the header
                const altTop = (parseInt(el.style.top || '0', 10) - popHeight - 16);
                if (altTop > margin) {
                  el.style.top = `${altTop}px`;
                } else {
                  el.style.top = `${Math.max(margin, window.innerHeight - popHeight - margin)}px`;
                }
              }
            }, 10);

            // Add outside click handler to close popover
            this.outsideClickHandler = (ev: Event) => {
              const target = ev.target as HTMLElement;
              // If click is inside the popover, ignore
              if (el.contains(target)) return;
              // If click is on another column header (filter icon or header area), ignore so the header click can open the next popover
              if (target.closest && target.closest('.column-header')) return;
              this.closeHeaderFilterPopover();
            };
            setTimeout(() => window.addEventListener('click', this.outsideClickHandler), 10);
          } catch (err) {
            console.warn('Failed to append popover to body', err);
          }
        }
      } else {
      }
    }, 30);
  }

  // Close popover without applying
  closeHeaderFilterPopover() {
    this.activeHeaderFilterField = null;

    // cleanup appended popover and outside handler
    try {
      if (this.outsideClickHandler) {
        window.removeEventListener('click', this.outsideClickHandler);
        this.outsideClickHandler = null;
      }
      if (this.popoverAppended && this.headerFilterPopover && this.headerFilterPopover.nativeElement) {
        const el = this.headerFilterPopover.nativeElement as HTMLElement;
        try {
          if (el.parentNode === document.body) {
            // Remove the appended node just in case Angular didn't (safe no-op if already removed)
            document.body.removeChild(el);
          }
        } catch (removeErr) {
          // ignore removal errors
        }
        // Reset appended flag
        this.popoverAppended = false;
      }
    } catch (e) {
      // ignore
    }
  }

  // Apply selection and notify parent
  applyHeaderFilter() {
    const field = this.activeHeaderFilterField;
    let value: any = null;
    if (field === 'amount') {
      value = { min: this.headerAmountMin, max: this.headerAmountMax };
    } else {
      value = this.headerSelectedValues;
    }
    // Mirror the change into our local copy of currentFilters immediately so the next header opening will see it
    try {
      if (!this.currentFilters) this.currentFilters = {} as any;
      switch (field) {
        case 'supplier':
          this.currentFilters.suppliers = value && value.length > 0 ? [...value] : null;
          break;
        case 'category':
          this.currentFilters.categories = value && value.length > 0 ? [...value] : null;
          break;
        case 'tags':
          this.currentFilters.tags = value && value.length > 0 ? [...value] : null;
          break;
        case 'date':
          this.currentFilters.invoiceDateRange = value && Array.isArray(value) && value.length === 2 ? [value[0], value[1]] : null;
          break;
        case 'tracking1':
          this.currentFilters.trackingOptions1 = value && value.length > 0 ? [...value] : null;
          break;
        case 'tracking2':
          this.currentFilters.trackingOptions2 = value && value.length > 0 ? [...value] : null;
          break;
        case 'submitter':
          this.currentFilters.submitters = value && value.length > 0 ? [...value] : null;
          break;
        case 'assignedTo':
          this.currentFilters.assignedTo = value && value.length > 0 ? [...value] : null;
          break;
        case 'type':
          this.currentFilters.types = value && value.length > 0 ? [...value] : null;
          break;
        case 'amount':
          this.currentFilters.amountMin = value?.min ?? null;
          this.currentFilters.amountMax = value?.max ?? null;
          break;
        default:
          break;
      }
    } catch (e) {
      // ignore mirror errors
    }
    this.headerFilterApplied.emit({ field, value });
    // Close and cleanup immediately so the next header can open cleanly
    this.closeHeaderFilterPopover();
  }

  // Helper to toggle a checkbox option in the inline header popover
  toggleHeaderFilterChoice(value: any, checked: boolean) {
    if (checked) {
      if (!this.headerSelectedValues.includes(value)) {
        this.headerSelectedValues = [...this.headerSelectedValues, value];
      }
    } else {
      this.headerSelectedValues = this.headerSelectedValues.filter(x => x !== value);
    }
  }

  selectAllFilteredOptions() {
    if (!this.activeHeaderFilterField) return;
    let options: any[] = [];
    switch (this.activeHeaderFilterField) {
      case 'supplier':
        options = this.supplierFilteredOptions || [];
        break;
      case 'tags':
        options = this.tagsFilteredOptions || [];
        break;
      case 'type':
        options = this.typeFilteredOptions || [];
        break;
      case 'category':
        options = (this.accounts || []).map(a => a.name);
        break;
      case 'submitter':
        options = (this.submitters || []) || [];
        break;
      case 'tracking1':
        options = (this.selectedTrackingCategories[0]?.options || []).map(o => o.name);
        break;
      case 'tracking2':
        options = (this.selectedTrackingCategories[1]?.options || []).map(o => o.name);
        break;
      default:
        options = [];
        break;
    }
    this.headerSelectedValues = Array.from(new Set([...(this.headerSelectedValues || []), ...options]));
  }

  clearHeaderSelection() {
    this.headerSelectedValues = [];
  }

  // Search helpers for popovers
  filterSupplierOptions(term: string) {
    this.supplierSearchTerm = term || '';
    if (!this.supplierSearchTerm) {
      this.supplierFilteredOptions = [...this.supplierDisplayOptions];
      return;
    }
    const t = this.supplierSearchTerm.trim().toLowerCase();
    this.supplierFilteredOptions = this.supplierDisplayOptions.filter(s => (s || '').toLowerCase().includes(t));
  }

  filterTagOptions(term: string) {
    this.tagsSearchTerm = term || '';
    if (!this.tagsSearchTerm) {
      this.tagsFilteredOptions = (this.tags || []).map(t => t.tagName || '').filter(s => !!s).sort((a,b)=> (a||'').localeCompare(b||''));
      // ensure categories available too when tags box is opened later
      this.categoriesFilteredOptions = (this.accounts || []).map(a => a.name || '').filter(s => !!s).sort((a,b)=> (a||'').localeCompare(b||''));
      return;
    }
    const t = this.tagsSearchTerm.trim().toLowerCase();
    this.tagsFilteredOptions = (this.tags || []).map(t=>t.tagName||'').filter(s => s.toLowerCase().includes(t)).sort((a,b)=> (a||'').localeCompare(b||''));
    this.categoriesFilteredOptions = (this.accounts || []).map(a => a.name || '').filter(s => !!s).sort((a,b)=> (a||'').localeCompare(b||''));
  }

  filterSubmitterOptions(term: string) {
    this.submitterSearchTerm = term || '';
    if (!this.submitterSearchTerm) {
      this.submitterFilteredOptions = [...this.submitterOptions];
      return;
    }
    const t = this.submitterSearchTerm.trim().toLowerCase();
    this.submitterFilteredOptions = this.submitterOptions.filter(s => (s || '').toLowerCase().includes(t));
  }

  filterTypeOptions(term: string) {
    this.typeSearchTerm = term || '';
    if (!this.typeSearchTerm) {
      this.typeFilteredOptions = [...this.typeOptions];
      return;
    }
    const t = this.typeSearchTerm.trim().toLowerCase();
    this.typeFilteredOptions = this.typeOptions.filter(s => (s || '').toLowerCase().includes(t));
  }

  // Handle date ranges selected in the inline popover
  onHeaderDateRangeSelected(range: any) {
    this.headerSelectedValues = [range.start, range.end];
  }

  columnMode = ColumnMode;
  statuses = InvoiceStatus;
  inProcessingRecord = inProcessingRecord;
  count = 0;
  showSidebar: boolean = false;
  isHorizontalDirection: boolean = false;
  loading: boolean = false;
  isPublishing: boolean = false;
  isPdf: boolean = false;
  tempFile: string;
  invoiceId: any;
  isHeaderCheckboxSelected: boolean = false;
  shownCategoriesInvoiceId: Guid = null;
  showTotalInvoiceId: Guid = null;
  shownTrackingOptions1InvoiceId: Guid = null;
  shownTrackingOptions2InvoiceId: Guid = null;

  trackingCategories: TrackingCategory[] = [];
  selectedTrackingCategoriesIds: string[] = [];
  screenBrakepoints = ScreenBrakepoints;
  currentPublishId: string = '';
  tooltipValue: string = '';
  defaultCurrency: CurrencyCode;
  showToolTipDelay: number = 500;
  private integrationType: IntegrationType = 'None';
  subscriptions: Subscription[] = [];
  hoveredMatchPillId: string = null;
  invoiceMatchDetails: Record<string, UnreconciledReport[]> = {};
  matchDetailsLoading: Record<string, boolean> = {};
  matchDetailsError: Record<string, string> = {};
  showSuggestedMatchesModal = false;
  selectedInvoiceForMatch: InvoiceShort = null;
  selectedTransactionIdForMatch: string = null;
  modalMatchProcessing = false;
  constructor(
    private _router: Router,
    private _toasterMessageService: ToasterMessageService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private _invoiceService: InvoiceService,
    private _inboxInvoicesStorage: InboxInvoicesStorageService,
    private _organizationService: OrganizationService,
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.getOrganizationDefaultCurrency();
    this.normalizeSubmitterNames();
    this.setIntegrationType();
    if (this.status === InvoiceStatus.inProcessing) {
      this.subscribeToAddingInvoice();
      this.subscribeToInvoiceUpdates();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pageSize'] && !changes['pageSize'].firstChange) {
      // Force change detection when pageSize input changes
    }
    this.normalizeSubmitterNames();

    // SD-1235: the category picker lists active accounts, plus any inactive one a row already
    // uses so its label still shows. Built once per input change, not per row per tick.
    if (changes['accounts'] || changes['invoices']) {
      const inUse = new Set((this.invoices || []).map(i => i?.accountId).filter(id => !!id));
      this.selectableAccounts = (this.accounts || []).filter(a => a.isActive === true || inUse.has(a.id));
    }

    // SD-1235: the host can clear the selection (the bar's "Clear"); untick the header box with it.
    if (changes['invoices'] && !(this.invoices || []).some(i => i?.checked)) {
      this.isHeaderCheckboxSelected = false;
    }

    // Recompute supplier and type options for inline filter popover
    this.supplierOptions = Array.from(new Set((this.invoices || [])
      .map(i => i?.supplierName)
      .filter(s => !!s)))
      .sort((a, b) => (a || '').localeCompare(b || ''));

    this.typeOptions = Array.from(new Set((this.invoices || [])
      .map(i => i?.type)
      .filter(s => !!s)))
      .sort((a, b) => (a || '').localeCompare(b || ''));

    // If explicit supplier list provided via input (from sidepanel), prefer that
    this.supplierDisplayOptions = (this.suppliers && this.suppliers.length > 0)
      ? this.suppliers.map(s => s.name).sort((a,b) => (a||'').localeCompare(b||''))
      : this.supplierOptions;

    // initialize filtered lists and submitter options
    this.supplierFilteredOptions = [...this.supplierDisplayOptions];
    this.typeFilteredOptions = [...this.typeOptions];
    this.submitterOptions = (this.submitters && this.submitters.length > 0) ? [...this.submitters] : Array.from(new Set((this.invoices || []).map(i => i?.submitterName).filter(s => !!s))).sort((a,b)=> (a||'').localeCompare(b||''));
    this.submitterFilteredOptions = [...this.submitterOptions];
    this.tagsFilteredOptions = (this.tags || []).map(t => t.tagName || '').filter(s => !!s).sort((a,b)=> (a||'').localeCompare(b||''));
  }

  // Maps a row's raw `type` to its display label. The inbox API usually sends a
  // friendly label already (e.g. "Supplier Invoice"), but for ACCREC (Customer
  // Invoice) it can come through as the raw accounting code — map those so the
  // Type column always reads correctly. Unknown / already-friendly values pass
  // through unchanged.
  getTypeDisplay(value: string): string {
    if (!value) return '';
    switch (value) {
      case 'ACCREC': return 'Customer Invoice';
      case 'ACCPAY': return 'Supplier Invoice';
      case 'ACCPAYCREDIT': return 'Credit Note';
      case 'EXPCLAIMS': return 'Expense Claim';
      case 'EXP': return 'Receipt';
      default: return value;
    }
  }

  private normalizeSubmitterNames() {
    if (this.invoices && Array.isArray(this.invoices)) {
      this.invoices.forEach(inv => {
        if (inv.submitterName === undefined || inv.submitterName === null || inv.submitterName === 'Submitter Name') {
          inv.submitterName = '';
        }
      });
    }
  }

  getOrganizationDefaultCurrency() {
    this._organizationService.getOrganizationDefaultCurrency(this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.defaultCurrency = response.currency;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }

  getCurrencyFlagUrl(code: string | number): string {
    return currencyFlagUrl(code);
  }

  showMoreCategories(invoice: InvoiceShort) {
    if (this.shownCategoriesInvoiceId == invoice.id) {
      this.shownCategoriesInvoiceId = null;
    } else {
      this.shownCategoriesInvoiceId = invoice.id;
    }
  }

  showMoreTrackingOptions(kind: string, invoice: InvoiceShort) {
    if (kind == '1') {
      if (this.shownTrackingOptions1InvoiceId == invoice.id) {
        this.shownTrackingOptions1InvoiceId = null;
      } else {
        this.shownTrackingOptions1InvoiceId = invoice.id;
      }
    } else {
      if (this.shownTrackingOptions2InvoiceId == invoice.id) {
        this.shownTrackingOptions2InvoiceId = null;
      } else {
        this.shownTrackingOptions2InvoiceId = invoice.id;
      }
    }
  }

  deleteTag(tag: any) {
    let tagToInvoices = {
      tagId: tag.tagId,
      tagName: tag.tagName,
      invoiceId: tag.invoiceId.toString(),
      invoiceProcessedStatus:
        this.status == InvoiceStatus.inProcessing ? false : true,
    } as DeletedTagFromInvoice;

    this.deleteTagEvent.emit(tagToInvoices);
  }

  onPageSizeChange(event: any) {
    const newPageSize = parseInt(event.target.value);
    this.pageSizeSelect.emit(newPageSize);
  }

  subscribeToInvoiceUpdates() {
    this._recognizedInvoiceService.invoiceState$.pipe(this.untilThis).subscribe(
      (invoice: Invoice) => {
        if (invoice?.referenceId && invoice.referenceId != Guid.EMPTY) {
          const index = this.invoices.lastIndexOf(inProcessingRecord);
          if (index !== -1) {
            this.invoices = this.invoices.filter(
              (i) => i != this.invoices[index]
            );
            this.invoices = [...this.invoices];
          }
        }
        this._addNewInvoice(invoice);
      },
      (e: Error) => {
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Error,
          'Error',
          e.message
        );
      }
    );
  }

  subscribeToAddingInvoice() {
    this._recognizedInvoiceService.inProcessingRecordState$
      .pipe(this.untilThis)
      .subscribe(
        (invoice: InvoiceShort) => {
          this.invoices = [invoice, ...this.invoices];
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  onSort(event) {
    let sortData = {
      value: event.newValue,
      columnName: event.column.name,
      property: event.column.prop,
    } as SortTableParameters;

    this.sortTableEvent.emit(sortData);
  }

  onActivate(event: any) {
    if (event.type !== 'click') return;
    else {
      if (event.column.name == 'Checker') {
        return;
      }

      if (event.column.name == 'Tags') {
        return;
      }

      if (event.column.name == 'Category') {
        return;
      }

      if (event.column.name === '') {
        this.invoiceId = event.row.id;
        this._getInvoice();
        return;
      }
    }

    if (event.row.invoiceNumber === inProcessingRecord.invoiceNumber) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Warning,
        'Warning',
        'This invoice is being processed!'
      );
      return;
    }

    this._inboxInvoicesStorage.fillInvoicesArray(
      this.invoices,
      event.row.id?.toString()
    );

    // In the combined view both states share one table, so the row's own
    // processed flag — not the table-level status — decides view vs edit.
    const openAsProcessed = this.status === this.statuses.combined
      ? !!event.row?.isProcessed
      : this.status === this.statuses.processed;
    // Open the detail in-place over the inbox; the host adds the `id` query
    // param and keeps the filtered table underneath.
    this.openInvoice.emit({ id: event.row.id?.toString(), editMode: !openAsProcessed });
  }

  onHeaderCheckboxChange() {
    this.invoices.forEach((invoice) => {
      invoice.checked = this.isHeaderCheckboxSelected;
    });
    // $('.detailpanel').css('height', 'calc(100vh - 95px)');
    this.rowCheckboxClickEvent.emit();
  }

  onCheckboxChange(event: any, row: InvoiceShort) {
    row.checked = event.target.checked;
    let checkedInvoices = this.invoices.filter((i) => i.checked == true);

    if (checkedInvoices?.length == 0) {
      this.isHeaderCheckboxSelected = false;
    }
    // $('.detailpanel').css('height', 'calc(100vh - 95px)');
    this.rowCheckboxClickEvent.emit();
  }

  private _addNewInvoice(invoice: Invoice) {
    const index = this.invoices.lastIndexOf(inProcessingRecord);

    if (index !== -1) {
      this.invoices[index] = invoice as unknown as InvoiceShort;
      this.invoices[index].shortCode = this.setShortCodeToUpdatedInvoice(
        this.invoices[index].supplierName
      );
      this.invoices[index].rowCount = 6;
      this.invoices = [...this.invoices];
    }
  }

  toggleFilter() {
    this.showSidebar = !this.showSidebar;
  }

  private setShortCodeToUpdatedInvoice(supplierName: string) {
    let result = '';
    if (supplierName && supplierName != '') {
      for (let w of supplierName.split(' ')) {
        result += w[0];
      }
      return result.slice(0, 3).toUpperCase();
    }
    return '';
  }

  private _getInvoice() {
    const id = this.invoiceId;
    if (id) {
      this.loading = true;
      this._invoiceService
        .getInvoiceById(id)
        .pipe(take(1))
        .subscribe(
          (invoice: Invoice) => {
            this._recognizedInvoiceService.sendExistingInvoice(invoice);
            this.loading = false;
          },
          (e: Error) => {
            this.loading = false;
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error,
              'Error',
              e.message
            );
          }
        );
    }
  }

  onMatchPillHover(invoiceId: string, isMatched: boolean = false) {
    if (!invoiceId) {
      return;
    }
    this.hoveredMatchPillId = invoiceId;
    if (this.invoiceMatchDetails[invoiceId] || this.matchDetailsLoading[invoiceId]) {
      return;
    }
    this.fetchMatchDetails(invoiceId, isMatched);
  }

  onMatchPillLeave(invoiceId: string) {
    if (this.hoveredMatchPillId === invoiceId) {
      this.hoveredMatchPillId = null;
    }
  }

  private fetchMatchDetails(invoiceId: string, isMatched: boolean) {
    this.matchDetailsLoading[invoiceId] = true;
    this.matchDetailsError[invoiceId] = '';
    // Goes through InvoiceService → HttpInternalService, which prefixes the
    // environment's apiURL. (Was hardcoded to the staging host.)
    this._invoiceService.getInvoiceById(invoiceId)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          const matched = response?.matchedTransactions;
          const unreconciled = response?.unreconciledData;
          if (isMatched && matched?.length) {
            this.invoiceMatchDetails[invoiceId] = matched;
          } else if (unreconciled?.length) {
            this.invoiceMatchDetails[invoiceId] = unreconciled;
          } else {
            this.invoiceMatchDetails[invoiceId] = matched || [];
          }
          this.matchDetailsLoading[invoiceId] = false;
        },
        () => {
          this.matchDetailsError[invoiceId] = isMatched
            ? 'Unable to load matched transaction.'
            : 'Unable to load potential matches.';
          this.matchDetailsLoading[invoiceId] = false;
        }
      );
  }

  openSuggestedMatchesModal(row: InvoiceShort, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!row?.id) {
      return;
    }
    this.selectedInvoiceForMatch = row;
    this.selectedTransactionIdForMatch = null;
    this.modalMatchProcessing = false;
    const invoiceId = row.id.toString();
    if (invoiceId && !this.invoiceMatchDetails[invoiceId] && !this.matchDetailsLoading[invoiceId]) {
      this.fetchMatchDetails(invoiceId, false);
    }
    this.showSuggestedMatchesModal = true;
  }

  closeSuggestedMatchesModal() {
    this.showSuggestedMatchesModal = false;
    this.selectedInvoiceForMatch = null;
    this.selectedTransactionIdForMatch = null;
    this.modalMatchProcessing = false;
  }

  selectSuggestedTransaction(transaction: UnreconciledReport) {
    if (!transaction?.unreconciledReportId) {
      return;
    }
    this.selectedTransactionIdForMatch = transaction.unreconciledReportId;
  }

  confirmSuggestedMatch() {
    if (!this.selectedInvoiceForMatch?.id || !this.selectedTransactionIdForMatch) {
      return;
    }
    const invoiceId = this.selectedInvoiceForMatch.id.toString();
    const organizationId = this._organizationService.selectedOrganization;
    if (!organizationId) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Error,
        'Error',
        'Organization missing. Please re-select your workspace.'
      );
      return;
    }
    const model = {
      unreconciledReportIds: this.selectedTransactionIdForMatch,
      invoiceId,
      organizationId,
    };
    this.modalMatchProcessing = true;
    this._invoiceService
      .updateSuggestedMatches(model)
      .pipe(take(1))
      .subscribe(
        () => {
          this.modalMatchProcessing = false;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Success,
            'Success',
            'Transaction matched to invoice.'
          );
          this.selectedInvoiceForMatch.isMatched = true;
          this.selectedInvoiceForMatch.suggestedMatchCount = 0;
          if (invoiceId) {
            const currentDetails = this.invoiceMatchDetails[invoiceId] || [];
            const matchedDetail = currentDetails.find(
              (detail) => detail.unreconciledReportId === this.selectedTransactionIdForMatch
            );
            this.invoiceMatchDetails[invoiceId] = matchedDetail ? [matchedDetail] : [];
          }
          this.closeSuggestedMatchesModal();
        },
        (e: Error) => {
          this.modalMatchProcessing = false;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            (e as any)?.error?.errorMessage || e?.message || 'Failed to match transaction.'
          );
        }
      );
  }

  clickOn(event: any) {
    this.pageSelect.emit(event.page as number);
  }

  publishInvoice(row: InvoiceShort) {
    if (!row) {
      return;
    }
    this.currentPublishId = row?.id?.toString() || '';
    this.isPublishing = true;
    this.publishInvoiceEvent.emit(row);
  }

  onPublishClick(row: InvoiceShort, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!row || row.isPublishing || !row.required_Fields) {
      return;
    }
    row.isPublishing = true;
    this.publishInvoice(row);
  }

  onUpdateClick(row: InvoiceShort, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!row) {
      return;
    }
    this._inboxInvoicesStorage.fillInvoicesArray(
      this.invoices,
      row.id?.toString()
    );
    this.openInvoice.emit({ id: row.id?.toString(), editMode: true });
  }

  account: PaymentAccount = new PaymentAccount();
  invoice: InvoiceShort = new InvoiceShort();

  onSelectAccount(row: InvoiceShort, event: PaymentAccount) {
    const id = row.id.toString();
    const accountId = event.id;
    if (id) {
      this.loading = true;
      this._invoiceService
        .getInvoiceById(id)
        .pipe(take(1))
        .subscribe(
          (invoice: Invoice) => {
            this.loading = false;
            if (event == undefined) {
              let data = {
                code: null,
                id: null,
                name: null,
                accountID: null,
              };
              this.account = data;
              this.saveInvoiceWithDelay('Account', 500, invoice, accountId);
            }
            let data = {
              code: event.code,
              id: event.id,
              name: event.name,
              accountID: event.id,
            };
            this.account = data;
            this.saveInvoiceWithDelay('Account', 500, invoice, accountId);
          },
          (e: Error) => {
            this.loading = false;
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error,
              'Error',
              e.message
            );
          }
        );
    }
  }

  saveInvoiceWithDelay(
    fieldName: string,
    delay: number = 500,
    invoice: Invoice,
    accountId: string
  ) {
    if (!invoice.processed) {
      setTimeout(() => {
        this.save(fieldName, invoice, accountId);
      }, delay);
    }
  }

  save(fieldName: string, invoice: Invoice, accountId: string) {
    this._invoiceService
      .updateInvoice(this.getInvoiceObject(true, invoice, accountId))
      .pipe(take(1))
      .subscribe(
        (response: boolean) => {
          this.invoices.find(
            (x) =>
              x.id.toString().toLowerCase() ==
              invoice.id.toString().toLowerCase()
          ).required_Fields = response;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Success,
            '',
            `Account Updated`
          );
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.ErrorInbox,
            '',
            `Error!`
          );
        }
      );
  }

  getInvoiceObject(
    isSaving: boolean = false,
    invoiceUpdate: Invoice,
    accountId: string
  ): CreatedInvoiceWithLines {
    let invoice = new CreatedInvoiceWithLines();
    invoice.invoiceLines = invoiceUpdate.invoiceLines;
    invoice.amountDue = invoiceUpdate.amountDue ?? 0;
    invoice.currency = invoiceUpdate.currency;
    invoice.date = invoiceUpdate.date;
    invoice.dueDate = isSaving
      ? invoiceUpdate.dueDate
      : !invoiceUpdate.dueDate
      ? invoiceUpdate.date
      : invoiceUpdate.dueDate;
    invoice.supplierName = invoiceUpdate.supplierName;
    invoice.supplierId = invoiceUpdate.supplierId;
    (invoice.id = invoiceUpdate.id.toString() ?? ''),
      (invoice.invoiceNumber =
        invoiceUpdate.invoiceNumber == '' ? null : invoiceUpdate.invoiceNumber);
    invoice.organisationId = invoiceUpdate.organisationId;
    invoice.paymentStatus = invoiceUpdate.paymentStatus;
    invoice.pdfUrl = invoiceUpdate.pdfUrl;
    invoice.subTotal = invoiceUpdate.subTotal ?? 0;
    invoice.totalTax = invoiceUpdate.totalTax ?? 0;
    invoice.type = invoiceUpdate.type;
    invoice.invoiceLines = invoiceUpdate.invoiceLines;
    invoice.InvoiceOrCreditNote = invoiceUpdate.type;
    invoice.paidBy = invoiceUpdate.paidBy;
    invoice.expenseReportId = invoiceUpdate.expenseReportId;
    invoice.expenseReportName = invoiceUpdate.expenseReportName;
    invoice.paymentAccountNumber = invoiceUpdate.paymentAccountNumber;
    invoice.paymentDate = invoiceUpdate.paymentDate;
    invoice.unreconciledReportIds = invoiceUpdate.unreconciledReportId;
    invoice.processed = invoiceUpdate.processed;
    if (invoice.invoiceLines?.length > 0) {
      invoice.invoiceLines[0].accountId = accountId;
      for (let line of invoice.invoiceLines) {
        line.organisationId = invoiceUpdate.organisationId;
        if (line.totalTax == null) {
          line.totalTax = 0;
        }
      }
    }
    invoice.requiredFields = this.validateForPublish(invoiceUpdate);
    return invoice;
  }

  validateForPublish(invoice: Invoice) {
    let requiredField: boolean = true;
    if (invoice.invoiceLines.length > 0) {
      if (!invoice.invoiceLines[0].accountId) requiredField = false;
      if (!invoice.invoiceLines[0].taxId) requiredField = false;
    } else requiredField = false;
    if (invoice.date == null) requiredField = false;
    if (invoice.dueDate == null) requiredField = false;
    if (!invoice.supplierId) requiredField = false;
    if (!invoice.invoiceNumber) requiredField = false;
    if (!invoice.currency) requiredField = false;
    if (invoice.type == 'EXPCLAIMS') {
      if (invoice.paidBy == null) requiredField = false;
      if (invoice.expenseReportId == null) requiredField = false;
    }
    if (invoice.type == 'EXP') {
      if (invoice.paymentAccountNumber == null) requiredField = false;
    }
    return requiredField;
  }

  private setIntegrationType() {
    this.subscriptions.push(
      this._organizationService.organizationList.subscribe((data) => {
        const org = data?.find(
          (o) => o.organisationID === this._organizationService.selectedOrganization
        );
        if (org?.accountingIntegration === AccountingIntegration.xero) {
          this.integrationType = 'Xero';
        } else if (org?.accountingIntegration === AccountingIntegration.QBO) {
          this.integrationType = 'QBO';
        } else {
          this.integrationType = 'None';
        }
      })
    );
  }

  getIntegrationType(): IntegrationType {
    return this.integrationType;
  }

  getPlaceholderStatus(invoice: InvoiceShort): string {
    // Simple placeholder logic - could be enhanced with real data
    const statuses: StatusType[] = ['Awaiting Payment', 'Paid', 'Voided'];
    // const hash = invoice.id.toString().length % 3;
    return fromCamelCase(invoice.status);
  }

  getMatchDetailsPreview(invoiceId: string, isMatched: boolean): UnreconciledReport[] {
    const details = this.invoiceMatchDetails[invoiceId] || [];
    if (!details?.length) {
      return [];
    }
    return isMatched ? details.slice(0, 1) : details.slice(0, 3);
  }

  isColumnDisplayed(columnKey: string): boolean {
    if (columnKey === 'submitter') {
      return this.showSubmitterColumn;
    }
    if (!this.columnVisibility) {
      return true;
    }
    const value = this.columnVisibility[columnKey];
    return value === undefined ? true : value;
  }

  shouldShowInvoiceActions(row: InvoiceShort): boolean {
    if (!row) {
      return false;
    }
    const showMatchedIcon = !!(row.isProcessed && row.isMatched);
    const canRenderPublishButton = !!(this.canPublish && !row.isProcessed);
    return showMatchedIcon || canRenderPublishButton;
  }
}
